import React, { useCallback, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import Text from '../../../library/text/text';

import { useTreadwellPreferences } from '../../../shared/hooks/hooks';

import { MILES_DRIVEN_ANNUALLY_DEFAULT_VALUE } from '../../../shared/constants/treadwell';

import styles from './annual-miles-input.scss';

const MAX_ANNUAL_MILES = 50000;
const MIN_ANNUAL_MILES = 0;

interface AnnualMilesInputProps {
  onChange?: (annualMiles) => void;
}

function AnnualMilesInput(props: AnnualMilesInputProps) {
  const { onChange } = props;
  const { saveTreadwellPreferences, treadwellPreferences } =
    useTreadwellPreferences();
  const [milesDrivenAnnually, setMilesDrivenAnnually] = useState(
    Number(
      treadwellPreferences?.selectedDrivingPreferences?.milesDrivenAnnually ||
        MILES_DRIVEN_ANNUALLY_DEFAULT_VALUE
    )
  );

  const debouncedSaveTreadwellPreferences = useMemo(
    () =>
      debounce((annualMiles, treadwellPreferences) => {
        treadwellPreferences &&
          saveTreadwellPreferences({
            ...treadwellPreferences,
            selectedDrivingPreferences: {
              ...treadwellPreferences.selectedDrivingPreferences,
              milesDrivenAnnually: annualMiles?.toString()
            }
          });
      }, 1000),
    [saveTreadwellPreferences]
  );

  const onChangeAnnualMiles = useCallback(
    e => {
      let annualMiles = parseInt(e.target.value);

      if (!isNaN(annualMiles)) {
        if (annualMiles < MIN_ANNUAL_MILES) {
          annualMiles = MIN_ANNUAL_MILES;
        } else if (annualMiles > MAX_ANNUAL_MILES) {
          annualMiles = MAX_ANNUAL_MILES;
        }
      } else {
        annualMiles = MIN_ANNUAL_MILES;
      }

      setMilesDrivenAnnually(annualMiles);
      onChange?.(annualMiles);
      debouncedSaveTreadwellPreferences(annualMiles, treadwellPreferences);
    },
    [debouncedSaveTreadwellPreferences, onChange, treadwellPreferences]
  );

  return (
    <Text
      className={styles['annual-miles-input']}
      inputMode="numeric"
      isRequired
      label="Estimated annual miles"
      name="annual-miles"
      onChange={onChangeAnnualMiles}
      type="number"
      value={milesDrivenAnnually?.toString()}
    />
  );
}

export default AnnualMilesInput;
